import { Nullable } from 'global';
import { ProposalProduct } from '@trnsact/trnsact-shared-types/src/generated';
import { FinanceProgram, LenderProfile, ProposalSaleType } from '@trnsact/trnsact-shared-types';

import { Section } from './types';
import { Equipment } from '../../equipment';
import {
  AftermarketMenu,
  AftermarketMenuOption,
  BuilderViewType,
  FormSections,
  OptionName,
  PaymentOptionTerm,
  Pricing,
  ProductConfig,
  ProductId,
  Submission,
  Term,
} from '../types';

const setProposalMenus = (payload: { menuOptions: AftermarketMenu[] }) =>
  ({
    type: 'DESKING_SET_PROPOSAL_MENUS',
    payload,
  }) as const;

const toggleIsProposalProductsLoading = (payload: { isLoading: boolean }) =>
  ({
    type: 'DESKING_TOGGLE_PROPOSALS_PRODUCTS_LOADING',
    payload,
  }) as const;

const resetMenuBuilder = () =>
  ({
    type: 'DESKING_RESET_MENU_BUILDER',
  }) as const;

const setProposalDetailsForEdit = (payload: { id: string; saleType: ProposalSaleType } | null) =>
  ({
    type: 'DESKING_SET_PROPOSAL_DETAILS_FOR_EDIT',
    payload,
  }) as const;

const updateMenuBuilderViewType = (payload: { type: BuilderViewType }) =>
  ({
    type: 'DESKING_UPDATE_MENU_BUILDER_VIEW_TYPE',
    payload,
  }) as const;

const setMenuToBuilder = (payload: { id: string; terms: string[] }) =>
  ({
    type: 'DESKING_SET_MENU_TO_BUILDER',
    payload,
  }) as const;

const setFinancePricing = (payload: { results: Pricing[] }) =>
  ({
    type: 'DESKING_SET_FINANCE_PRICING',
    payload,
  }) as const;

const setMenuPricing = (payload: { results: Pricing[] }) =>
  ({
    type: 'DESKING_SET_MENU_PRICING',
    payload,
  }) as const;

const removeFinancePricing = (payload: { terms: Term[] }) =>
  ({
    type: 'DESKING_REMOVE_FINANCE_PRICING',
    payload,
  }) as const;

const removeMenuPricing = (payload: { terms: Term[] }) =>
  ({
    type: 'DESKING_REMOVE_MENU_PRICING',
    payload,
  }) as const;

const setFinancePrograms = (payload: { programs: FinanceProgram[] }) =>
  ({
    type: 'DESKING_SET_FINANCE_PROGRAMS',
    payload,
  }) as const;

const setEquipmentData = (payload: { equipments: Equipment[] }) =>
  ({
    type: 'DESKING_SET_EQUIPMENT_DATA',
    payload,
  }) as const;

const setCurrentEquipmentData = (payload: { id: string }) =>
  ({
    type: 'DESKING_SET_CURRENT_EQUIPMENT_DATA',
    payload,
  }) as const;

const updateProducts = (payload: { products: ProposalProduct[] }) =>
  ({
    type: 'DESKING_UPDATE_PROPOSALS_PRODUCTS',
    payload,
  }) as const;

const setProducts = (payload: { products: ProposalProduct[]; netFinanceAmount?: number }) =>
  ({
    type: 'DESKING_SET_PROPOSALS_PRODUCTS',
    payload,
  }) as const;

const removeProductFromList = (payload: { productId: string }) =>
  ({
    type: 'DESKING_REMOVE_PRODUCT_FROM_LIST',
    payload,
  }) as const;

const setMenuOptions = (payload: { menuOptions: AftermarketMenuOption[] }) =>
  ({
    type: 'DESKING_SET_MENU_OPTIONS',
    payload,
  }) as const;

const setOrderOfProductInMenu = (payload: { menuName: string; from: number; to: number }) =>
  ({
    type: 'DESKING_SET_ORDER_OF_PRODUCT_IN_MENU',
    payload,
  }) as const;

const unselectProduct = (payload: { productId: string }) =>
  ({
    type: 'DESKING_UNSELECT_PROPOSALS_PRODUCTS',
    payload,
  }) as const;

const setMenuTerm = (payload: Partial<PaymentOptionTerm>) =>
  ({
    type: 'DESKING_SET_MENU_TERM',
    payload,
  }) as const;

const addMenuProduct = (payload: { menuType: string; product: ProposalProduct }) =>
  ({
    type: 'DESKING_ADD_PRODUCT',
    payload,
  }) as const;

const removeMenuProduct = (payload: { menuName: string; productId: string }) =>
  ({
    type: 'DESKING_REMOVE_PRODUCT',
    payload,
  }) as const;

const updateRecalculate = () =>
  ({
    type: 'DESKING_UPDATE_RECALCULATE',
  }) as const;

const updateSectionLayout = (payload: { section: FormSections; key: keyof Section; value: boolean }) =>
  ({
    type: 'DESKING_UPDATE_SECTION_LAYOUT',
    payload,
  }) as const;

const selectMenuOption = (payload: { optionName: string }) =>
  ({
    type: 'DESKING_LAYOUT_SELECT_MENU_OPTION',
    payload,
  }) as const;

const resetStructureAndMenuOptions = () =>
  ({
    type: 'DESKING_RESET_STRUCTURE_AND_MENU_OPTIONS',
  }) as const;

const clearState = () =>
  ({
    type: 'DESKING_CLEAR',
  }) as const;

const updateProductConfiguration = (payload: { productId: string; data: Partial<ProductConfig> }) =>
  ({
    type: 'DESKING_UPDATE_PRODUCT_CONFIGURATION',
    payload,
  }) as const;

const updateAllSelectedProductsConfiguration = (payload: { data: Partial<ProductConfig> }) =>
  ({
    type: 'DESKING_UPDATE_ALL_PRODUCTS_CONFIGURATION',
    payload,
  }) as const;

const updateProductConfigurationInMenu = (payload: {
  productId: string;
  menuName: string;
  data: Partial<ProductConfig>;
}) =>
  ({
    type: 'DESKING_UPDATE_PRODUCT_CONFIGURATION_IN_MENU',
    payload,
  }) as const;

const setProductsConfigFromExistingMenu = (payload: { terms: string[]; data: AftermarketMenuOption[] }) =>
  ({
    type: 'DESKING_SET_PRODUCTS_CONFIGURATION_FROM_EXISTING_MENU',
    payload,
  }) as const;

const addSelectedProductsToMenu = () =>
  ({
    type: 'DESKING_ADD_SELECTED_PRODUCTS_TO_MENU',
  }) as const;

const runJsonEngine = () =>
  ({
    type: 'DESKING_RUN_ENGINE',
  }) as const;

const addMenuTerm = (payload: { term: string; menuId: string }) =>
  ({
    type: 'DESKING_ADD_TERM_FROM_MENU_BUILDER',
    payload,
  }) as const;

const removeMenuTerm = (payload: { term: string }) =>
  ({
    type: 'DESKING_REMOVE_TERM_FROM_MENU_BUILDER',
    payload,
  }) as const;

const removeProductsFromMenu = (payload: { ids: string[] }) =>
  ({
    type: 'DESKING_REMOVE_PRODUCTS_FROM_MENU',
    payload,
  }) as const;

const toggleIsEditEquipmentStatus = (payload: { status: boolean }) =>
  ({
    type: 'DESKING_TOGGLE_IS_EDIT_EQUIPMENT',
    payload,
  }) as const;

const setSubmissions = (payload: { submissions: Submission[] }) =>
  ({
    type: 'DESKING_SET_SUBMISSIONS',
    payload,
  }) as const;

const selectSubmission = (payload: { submission: Nullable<Submission> }) =>
  ({
    type: 'DESKING_SELECT_SUBMISSION',
    payload,
  }) as const;

const toggleSubmissionLoading = (payload: { status: boolean }) =>
  ({
    type: 'DESKING_TOGGLE_SUBMISSION_LOADING',
    payload,
  }) as const;

const setLenderProfilesWithOca = (payload: { accountName: string; lenderProfiles: LenderProfile[] }) =>
  ({
    type: 'DESKING_SET_LENDER_PROFILES_WITH_OCA',
    payload,
  }) as const;

const restoreBuilder = (payload: {
  builder: Record<Term, AftermarketMenu>;
  commonProductsConfiguration: Partial<ProductConfig>;
  productsConfig: Record<OptionName, Record<ProductId, ProductConfig>>;
}) =>
  ({
    type: 'DESKING_RESTORE_BUILDER',
    payload,
  }) as const;

const selectFinanceOption = (payload: { term: Nullable<Term> }) =>
  ({
    type: 'DESKING_SELECT_FINANCE_OPTION',
    payload,
  }) as const;

const addCustomProduct = (payload: { product: ProposalProduct }) =>
  ({
    type: 'DESKING_ADD_CUSTOM_PRODUCT',
    payload,
  }) as const;

const setFPDGenValue = (payload: { status: boolean }) =>
  ({
    type: 'DESKING_SET_FPD_GEN_VALUE',
    payload,
  }) as const;

const setIsFinanceDetailsEditable = (payload: { status: boolean }) =>
  ({
    type: 'DESKING_SET_IS_FINANCE_DETAILS_EDITABLE',
    payload,
  }) as const;

const setParentProposalId = (payload: { parentProposalId: Nullable<string> }) =>
  ({
    type: 'DESKING_SET_PARENT_PROPOSAL_ID',
    payload,
  }) as const;

const setIsProposalDataLoaded = (payload: { isLoaded: boolean }) =>
  ({
    type: 'DESKING_SET_IS_PROPOSAL_DATA_LOADED',
    payload,
  }) as const;

export const deskingActions = {
  restoreBuilder,
  setProposalDetailsForEdit,
  removeProductsFromMenu,
  unselectProduct,
  resetMenuBuilder,
  setProposalMenus,
  setFinancePricing,
  removeMenuTerm,
  setMenuOptions,
  setFinancePrograms,
  setEquipmentData,
  setProducts,
  removeProductFromList,
  addMenuProduct,
  clearState,
  setMenuTerm,
  runJsonEngine,
  updateProducts,
  selectMenuOption,
  removeMenuProduct,
  updateRecalculate,
  setMenuPricing,
  setMenuToBuilder,
  setCurrentEquipmentData,
  setOrderOfProductInMenu,
  addSelectedProductsToMenu,
  updateSectionLayout,
  updateProductConfiguration,
  resetStructureAndMenuOptions,
  toggleIsProposalProductsLoading,
  updateProductConfigurationInMenu,
  setProductsConfigFromExistingMenu,
  updateAllSelectedProductsConfiguration,
  removeFinancePricing,
  removeMenuPricing,
  updateMenuBuilderViewType,
  addMenuTerm,
  toggleIsEditEquipmentStatus,
  setSubmissions,
  selectSubmission,
  toggleSubmissionLoading,
  selectFinanceOption,
  addCustomProduct,
  setLenderProfilesWithOca,
  setFPDGenValue,
  setIsFinanceDetailsEditable,
  setParentProposalId,
  setIsProposalDataLoaded,
};
